/**
 * src/pages/RenFestSelfiePage.jsx
 * ═══════════════════════════════════════════════════════════════════════════
 * THE BOOTH — the shell that holds the three RenFest rooms.
 *
 *   selfie  · the guest experience
 *   shop    · the Apothecary Shoppe, to browse while a portrait paints
 *   ledger  · the operator's back room. ADMIN ONLY.
 *
 * ⚠️ FULL-VIEWPORT LAYER, OVER THE APP SHELL.
 * `fixed inset-0` at z-[60] clears the sidebar and bottom tabs (both z-40).
 * Same pattern as /inventory/tv. The shell stays mounted underneath — this is
 * deliberately a COVER, not a removal — so routing, auth and the z-80 alert
 * gate all keep working.
 *
 * ⚠️ THE ROOMS ARE A VIEW SWITCH, NOT A ROUTE.
 * A route change would unmount RenFestSelfie mid-generation, killing the poll
 * and losing the guest's portrait — the same rule that made Download a blob
 * rather than a link. Browsing the Shoppe while a portrait paints is the whole
 * point, and it only works if the selfie component is still mounted.
 *
 * ⚠️ WHICH IS WHY THE SELFIE ROOM IS HIDDEN, NOT UNMOUNTED, when the guest is
 * elsewhere. `display:none` keeps the poll loop, the countdown and the streamed
 * partials alive; a conditional render would throw all three away.
 *
 * ⚠️ THE ?admin FLAG IS COSMETIC. It decides whether the Ledger BUTTON is worth
 * drawing, nothing more. `renfest-selfie-status` refuses the ledger's data —
 * every guest's portraits, their emails, and the signed source frames — to any
 * request without a valid operator secret in the header (D-28 §3). Showing the
 * button to the wrong person leaks nothing.
 *
 * ⚠️ AND THE UNLOCK IS A SEPARATE STEP, ON PURPOSE. The source app had accounts:
 * `user?.role === 'admin'` and the tab appeared. There are no accounts here, so
 * staff type a shared secret once per tab — see lib/operator.js for why it is
 * sessionStorage and why it is not in the URL.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useEffect, useState, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { BoothStyles, BoothBar, BoothProgress } from '../components/features/renfest/boothChrome';
import RenFestSelfie from './marketing/RenFestSelfie';
import RenFestShop from './marketing/RenFestShop';
import RenFestSelfieAdmin from './marketing/RenFestSelfieAdmin';
import { hasOperatorKey, setOperatorKey, clearOperatorKey } from '../lib/operator';
import { installGlobalReporting } from '../lib/report';
import { capturePlacement } from '../lib/placement';
import { reportVisit } from '../lib/visit';
import { THEME } from '../lib/boothTheme';

export default function RenFestSelfiePage() {
  /* ⚠️ INSTALLED ON THE BOOTH ROUTE, NOT GLOBALLY. A crash on the marketing
     page has no booth session and nothing to attach itself to; a crash in front
     of a guest mid-portrait is the one we have never been able to see. */
  useEffect(() => { installGlobalReporting(); }, []);

  const { dark } = useTheme();
  /* ⚠️ THE SIGN DECIDES WHICH ROOM YOU LAND IN. Joel, 2026-09-05: "I need to
     be able to have a link just for the shopping page ... it doesn't take them
     to the selfie app, and it allows them to just see the shop, but they still
     have the selfie button."

     A shop QR on the counter and a selfie card on an A-frame are two different
     invitations, and until now both opened the camera. Somebody who scanned to
     look at products got a consent screen and a viewfinder, which reads as the
     wrong app.

     `?view=shop` (or the shorter `?shop`) opens the Shoppe instead. The selfie
     room is still mounted and one tap away on the bar, so this changes where a
     guest starts and takes nothing away.

     ⚠️ READ ONCE, AT MOUNT. Making this a live URL binding would fight the
     bar: a guest taps Free selfie, the URL still says shop, and the next
     render throws them back to the Shoppe. The link is a starting point, not
     the state. */
  const [view, setView] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const want = q.has('shop') ? 'shop' : (q.get('view') || '').toLowerCase();
    /* A themed event with no Shoppe ignores a shop link rather than opening a
       RenFest shelf at somebody's birthday party. See boothTheme.js. */
    if (THEME?.shop === false) return 'selfie';
    return want === 'shop' ? 'shop' : 'selfie';
  });
  const [chrome, setChrome] = useState(true);
  const onChrome = useCallback((v) => setChrome(v), []);
  const goShop = useCallback(() => setView('shop'), []);
  const hasShop = THEME?.shop !== false;

  /* ⚠️ THE PROGRESS OF A RUNNING PORTRAIT, LIFTED ONE LEVEL SO IT SURVIVES THE
     ROOM SWITCH. The selfie room is HIDDEN and not unmounted (see the top of
     this file), which keeps the poll alive — but `display:none` takes the bar
     inside the picture with it, so a guest browsing the Shoppe lost every
     signal that anything was happening. A guest who cannot see progress
     assumes it is broken, and the fix they reach for is a reload, which is how
     a portrait actually gets lost. Joel, 2026-08-27: "Need it global."

     ⚠️ RenFestSelfie STILL OWNS THE RUN. What travels up here is the derived
     percentage it is already showing, nothing else — one source, two
     renderings, so the two bars cannot contradict each other.

     ⚠️ setProgress IS STABLE, and that is load-bearing. An unstable callback
     would change identity on every render of this page, re-firing the child's
     effect in a loop. It re-renders RenFestSelfie about once a second while a
     portrait paints; a re-render is harmless, an UNMOUNT would end the run. */
  const [progress, setProgress] = useState(null);
  const onProgress = useCallback((p) => setProgress(p), []);

  /* ⚠️ COURTESY, NOT A GATE. The Ledger button is hidden unless ?admin=1 is on
     the URL, but the SERVER is what actually authorises the data — an operator
     credential checked in the function, never a client-side flag. Carried over
     from the source app, where the same property held via user_roles. Showing
     the button to the wrong person leaks nothing. */
  /* ⚠️ CAPTURED HERE, AT THE TOP OF THE BOOTH, BECAUSE THIS IS THE ONLY MOMENT
     THE QUERY STRING EXISTS. React Router drops it on the first navigation and
     the booth moves through several steps before it calls generate. Read on
     arrival, keep it for the visit. See src/lib/placement.js. */
  capturePlacement();

  /* ⚠️ THE DENOMINATOR (sql/043). Every rate we care about — paid per arrival,
     prints per arrival, revenue per arrival — needs a count of people who
     turned up, and until now nothing recorded one: `booth_run` is written when
     a portrait is GENERATED, so a guest who reads the price and walks away
     left no trace. That guest is the entire subject of a price test.

     ⚠️ FIRE AND FORGET, AFTER PLACEMENT, IN AN EFFECT WITH NO DEPS. It must run
     after `capturePlacement()` so the `?p=` slug is already stored, and it must
     never block or throw — an impression is worth nothing next to a portrait.
     `reportVisit` guards itself against StrictMode's double-invoke and against
     this page re-rendering every second while a portrait paints. */
  useEffect(() => { reportVisit(); }, []);

  const isAdmin = new URLSearchParams(window.location.search).has('admin');

  /* Unlocked for THIS TAB only. Closing it locks the booth phone again. */
  const [unlocked, setUnlocked] = useState(hasOperatorKey);
  const [keyInput, setKeyInput] = useState('');
  const [keyErr, setKeyErr] = useState('');

  const unlock = useCallback((e) => {
    e?.preventDefault?.();
    if (!setOperatorKey(keyInput)) { setKeyErr('Enter the operator key.'); return; }
    setKeyInput(''); setKeyErr(''); setUnlocked(true);
  }, [keyInput]);

  const lock = useCallback(() => { clearOperatorKey(); setUnlocked(false); }, []);

  return (
    /* ⚠️ FLEX COLUMN, NOT A SCROLLING BLOCK.
       The bar is a fixed-height row and the content takes exactly what is left
       (`flex-1 min-h-0`). That is what makes "nothing important below the fold"
       structural: a wizard step is sized to 100% of the content box, so it
       CANNOT overflow, while the Shoppe and the Ledger scroll normally inside
       the same box. The previous version was one long scrolling column and the
       welcome screen's Start button fell off the bottom of a phone. */
    <div
      className={`fixed inset-0 z-[60] flex flex-col${THEME?.rootClass ? ` ${THEME.rootClass}` : ''}`}
      style={{
        background: THEME?.pageBg ? THEME.pageBg : dark
          ? 'radial-gradient(80% 50% at 50% 0%, rgba(120,80,40,.30), transparent 70%), #100e0b'
          : 'radial-gradient(80% 50% at 50% 0%, rgba(214,178,124,.45), transparent 70%), #efe6d6',
      }}
    >
      <BoothStyles />

      {/* ⚠️ THE SELFIE ROOM HIDES THE BAR; THE OTHER ROOMS ALWAYS SHOW IT.
          Without the `view !== 'selfie'` half, a guest who taps "browse the
          Shoppe while you wait" lands in a room with no way back - the bar is
          suppressed because the selfie step is still `painting`. That is a
          trap, and it is the exact failure mode the no-navigation rule exists
          to prevent. */}
      {(view !== 'selfie' || chrome) && (hasShop || isAdmin) && (
        <div className="shrink-0 px-3 pt-3 pb-2">
          <div className="max-w-6xl mx-auto">
            <BoothBar view={view} onView={setView} isAdmin={isAdmin} />
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto rf-scroll px-3 pb-3">
        <div className="max-w-6xl mx-auto h-full">
          {/* Hidden, not unmounted — see the note at the top of this file. */}
          <div className={view === 'selfie' ? 'h-full' : 'hidden'}>
            <RenFestSelfie onChrome={onChrome} onShop={hasShop ? goShop : undefined} onProgress={onProgress} />
          </div>

          {/* Neither of these holds live state worth preserving, so they are
              cheap to mount on demand and keep the booth's first paint light. */}
          {view === 'shop' && hasShop && <RenFestShop />}
          {view === 'ledger' && isAdmin && (unlocked ? (
            <>
              <div className="flex justify-end mb-2">
                <button type="button" className="rf-chip" onClick={lock}>Lock</button>
              </div>
              <RenFestSelfieAdmin onLocked={lock} />
            </>
          ) : (
            /* ⚠️ THE SERVER IS THE GATE. This form does not check anything - it
               stores what was typed and lets the next request fail or succeed on
               its own merits. Nothing here decides access, so there is nothing
               here worth attacking. */
            <form onSubmit={unlock} className="max-w-sm mx-auto pt-10 text-center">
              <p className="rf-big" style={{ fontSize: '1.5rem', color: '#f7e4bb' }}>Staff only</p>
              <p className="rf-ink mb-4" style={{ color: 'rgba(247,228,187,.6)', fontSize: '.85rem' }}>
                The Ledger shows every guest&apos;s portrait and the photographs behind it.
              </p>
              <input
                type="password"
                value={keyInput}
                onChange={(e) => { setKeyInput(e.target.value); setKeyErr(''); }}
                placeholder="Operator key"
                autoComplete="off"
                className="w-full rounded-xl px-4 py-3 mb-3"
                style={{ background: 'rgba(0,0,0,.35)', color: '#f7e4bb', border: '1px solid rgba(247,228,187,.25)' }}
              />
              {keyErr && (
                <p className="rf-ink mb-2" style={{ color: '#f6a6a6', fontSize: '.8rem' }}>{keyErr}</p>
              )}
              <button type="submit" className="rf-big w-full rounded-2xl px-4 py-4" style={{ fontSize: '1.2rem' }}>
                Unlock
              </button>
              <p className="rf-ink mt-3" style={{ color: 'rgba(247,228,187,.45)', fontSize: '.72rem' }}>
                Unlocks this tab only. Close it and the booth phone is locked again.
              </p>
            </form>
          ))}
        </div>
      </div>

      {/* ⚠️ A ROW IN THE COLUMN, NOT AN OVERLAY, AND IT IS THE LAST CHILD ON
          PURPOSE. The scroll box above is `flex-1 min-h-0`, so a `shrink-0` row
          here takes its height OUT of the scrollable area rather than sitting
          on top of it: it cannot cover the Shoppe's last item, and it cannot be
          clipped by a container whose CSS was written for something else
          (`R-31` — a caption vanished exactly that way).

          ⚠️ ONLY OUTSIDE THE SELFIE ROOM. On the selfie view the bar inside the
          picture is already saying this, and two bars stacked is noise.

          ⚠️ IT DOES NOT MOVE THE GUEST WHEN THE PORTRAIT LANDS. It changes what
          it says and waits to be tapped — Joel, 2026-08-27: "No yanking them on
          done, leave them in the state they are in, but flag to them picture is
          done." */}
      {view !== 'selfie' && progress && (
        <div className="shrink-0 px-3 pb-3">
          <div className="max-w-6xl mx-auto">
            <BoothProgress progress={progress} onOpen={() => setView('selfie')} />
          </div>
        </div>
      )}
    </div>
  );
}
